import type { FormEvent } from "react";

import { AccountController } from "@repo/baseCloud";
import { log } from "@repo/helpers";
import { OneTimePasswordPage } from "@repo/ui";
import { useContext, useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";

import { AuthContext } from "./context";
import { useAuthSettings, useMfaTypes } from "./hooks";
import { toSameOriginPath } from "./returnUrl";

export function TLPCloudOTPPage() {
	const { username, returnUrl, selectedMfaType, rememberMe, clearStorage } = useContext(AuthContext);
	const [otp, setOtp] = useState<string>("");
	const [disabled, setDisabled] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [success, setSuccess] = useState<string>("");
	const [loggingIn, setLoggingIn] = useState<boolean>(false);
	const [resendLoading, setResendLoading] = useState<boolean>(false);
	const [rememberMachine, setRememberMachine] = useState<boolean>(true);
	const [pasted, setPasted] = useState<boolean>(false);
	const history = useHistory();
	const authSettingsQuery = useAuthSettings(username);
	const typesQuery = useMfaTypes(username);

	useEffect(() => {
		document.title = "TLP Cloud - Enter Multi Factor Authentication Code";
	}, []);

	async function resendCode() {
		setResendLoading(true);
		setError("");
		setSuccess("");
		const response = await AccountController.requestMfaCode(username, selectedMfaType);
		if (!response.success) {
			setError(response.message ?? "An error occurred while resending your multi factor code");
		} else {
			setSuccess("Multi Factor Code resent");
		}
		setResendLoading(false);
	}

	async function submit(event?: FormEvent<HTMLFormElement>) {
		event?.preventDefault();
		if (!otp || (event && (disabled || loggingIn)) || (!event && loggingIn)) return;
		setDisabled(true);
		setLoggingIn(true);
		await login();
	}

	useEffect(() => {
		if (otp.length < 6) {
			setDisabled(true);
		} else {
			setDisabled(false);
			if (pasted) {
				submit();
				setPasted(false);
			}
		}
	}, [otp]);

	function handleError(error: any) {
		log(error, "error");
		setError("An error occurred while logging in");
		setDisabled(false);
		setLoggingIn(false);
	}

	async function login() {
		setError("");
		setSuccess("");
		try {
			const response = await AccountController.oneTimePassword(username, otp, rememberMachine);

			if (!response.success) {
				setError(response.message ?? "Invalid username or password");
				setDisabled(false);
				setLoggingIn(false);
			} else {
				if (!rememberMe) clearStorage();
				window.location.replace(toSameOriginPath(returnUrl) ?? response.returnUrl);
			}
		} catch (error) {
			handleError(error);
		}
	}

	if (!username) {
		return <Redirect to="/Login" />;
	}

	const selected = typesQuery.data?.find((t) => t.id === selectedMfaType);

	return (
		<OneTimePasswordPage
			password={otp}
			setPassword={setOtp}
			onPaste={() => setPasted(true)}
			submitFunction={submit}
			resendCode={resendCode}
			resendLoading={resendLoading}
			type={selectedMfaType}
			obfuscation={selected?.obfuscation}
			rememberMe={authSettingsQuery.data?.canStoreMachine ? rememberMachine : undefined}
			setRememberMe={authSettingsQuery.data?.canStoreMachine ? setRememberMachine : undefined}
			disabled={disabled}
			loading={loggingIn}
			error={error}
			success={success}
			goBack={() => history.goBack()}
			backgroundImage={authSettingsQuery.data?.backgroundImage}
			appLogo={authSettingsQuery.data?.companyLogo}
		/>
	);
}
